import { Icon, type IconProps } from './Icon'

/**
 * The Chow glyph family.
 *
 * One grid, one stroke weight, equal optical weight. Each glyph forwards every
 * `IconProps` (size, title, className, style, event handlers) to the shared
 * `Icon` frame. Add new glyphs here so the whole set stays consistent.
 */

export const BikeIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="5.5" cy="17" r="3.25" />
    <circle cx="18.5" cy="17" r="3.25" />
    <path d="M5.5 17 9.5 9.5h5l2 4h2" />
    <path d="M9.5 9.5 12.5 17" />
    <path d="M13.5 7h2.5" />
  </Icon>
)

export const BowlIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3.5 11.5h17" />
    <path d="M5 11.5a7 7 0 0 0 14 0" />
    <path d="M9.5 4.5c0 1.1 1 1.4 1 2.6" />
    <path d="M14.5 4.5c0 1.1-1 1.4-1 2.6" />
  </Icon>
)

export const BoltIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M13 2.5 4.5 13.5H11l-1 8 8.5-11.5H12l1-7.5Z" />
  </Icon>
)

/* A wallet, not a card: a pouch with a fold-over flap and a clasp. The old
   drawing was a rounded rectangle with a horizontal rule, which is what a card
   is — so a wallet, a card and a bank all rendered as the same grey rectangle
   in the payment-methods list. */
export const WalletIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 8.5A2.5 2.5 0 0 1 6.5 6h9.9a1.6 1.6 0 0 1 1.6 1.6v.9" />
    <path d="M4 8.5v8A2.5 2.5 0 0 0 6.5 19h11.9a1.6 1.6 0 0 0 1.6-1.6v-6.8a1.6 1.6 0 0 0-1.6-1.6H6.5A2.5 2.5 0 0 1 4 8.5Z" />
    <circle cx="16.25" cy="14" r="1.15" />
  </Icon>
)

/* A bank: pediment, columns, plinth. Reads as "transfer to an account" in a way
   a second card glyph cannot. */
export const BankIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3.5 9.5 12 5l8.5 4.5" />
    <path d="M5 9.5v8M9.6 9.5v8M14.4 9.5v8M19 9.5v8" />
    <path d="M3 19.5h18" />
  </Icon>
)

export const PinIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" />
    <circle cx="12" cy="10" r="2.5" />
  </Icon>
)

export const CameraIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 8.5h3l1.5-2h7L17 8.5h3a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z" />
    <circle cx="12" cy="13" r="3.25" />
  </Icon>
)

export const CloseIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 6 18 18" />
    <path d="M18 6 6 18" />
  </Icon>
)

export const RouteIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="6" cy="18.5" r="2.25" />
    <circle cx="18" cy="5.5" r="2.25" />
    <path d="M8.25 18.5H14a3 3 0 0 0 0-6H10a3 3 0 0 1 0-6h2" />
  </Icon>
)

export const ClockIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </Icon>
)

export const ShieldIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3 5 6v5c0 4 3 7 7 9 4-2 7-5 7-9V6l-7-3Z" />
    <path d="M9.3 12l1.9 1.9 3.5-3.9" />
  </Icon>
)

export const StarIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="m12 3.6 2.55 5.2 5.75.85-4.15 4.05.98 5.7L12 16.6l-5.13 2.7.98-5.7L3.7 9.65l5.75-.85Z" />
  </Icon>
)

export const SearchIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.2-4.2" />
  </Icon>
)

export const ChevronRightIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="m9 6 6 6-6 6" />
  </Icon>
)

export const CheckIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="m5 12.5 4.5 4.5L19 7" />
  </Icon>
)

/* ── Emoji-parity nav + UI glyphs (Chow design-system cut) ───────────── */

export const HomeIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 10.5 12 4l8 6.5" />
    <path d="M5.5 9.5V19a1 1 0 0 0 1 1H10v-5h4v5h3.5a1 1 0 0 0 1-1V9.5" />
  </Icon>
)

export const ChefHatIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M7 14a3.5 3.5 0 0 1-1-6.86A4 4 0 0 1 13.7 6 3.6 3.6 0 0 1 18 7.2 3.5 3.5 0 0 1 17 14" />
    <path d="M7 14v4.5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V14" />
    <path d="M9.5 14v3M14.5 14v3" />
  </Icon>
)

export const PlateIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="4" />
  </Icon>
)

export const StorefrontIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 9.5 5.2 5h13.6L20 9.5" />
    <path d="M4 9.5a2.2 2.2 0 0 0 4 0 2.2 2.2 0 0 0 4 0 2.2 2.2 0 0 0 4 0 2.2 2.2 0 0 0 4 0" />
    <path d="M5.5 11.5V19a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-7.5" />
    <path d="M9.5 20v-4.5h5V20" />
  </Icon>
)

export const CardIcon = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="6" width="18" height="12" rx="2.5" />
    <path d="M3 10h18" />
    <path d="M6.5 14.5h4" />
  </Icon>
)

export const ChatIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H9l-4 3.5V6a1 1 0 0 1 1-1Z" />
    <path d="M8.5 10.5h.01M12 10.5h.01M15.5 10.5h.01" />
  </Icon>
)

export const LifebuoyIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="3.5" />
    <path d="m6 6 3.5 3.5M14.5 14.5 18 18M18 6l-3.5 3.5M9.5 14.5 6 18" />
  </Icon>
)

export const BasketIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 9.5h16l-1.3 8.2a2 2 0 0 1-2 1.8H7.3a2 2 0 0 1-2-1.8L4 9.5Z" />
    <path d="m8.5 9.5 2-5M15.5 9.5l-2-5" />
    <path d="M10 13v2.5M14 13v2.5" />
  </Icon>
)

export const FlameIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3.5c.6 3-1.8 4-1.8 6.4a2 2 0 0 0 3.7 1c.4 1 .9 1.4.9 2.8a3.8 3.8 0 0 1-7.6.2c0-2.7 2-3.6 1.8-6 1.6.5 2.2 1.6 3 .8.8-.8.7-2.6 0-5.2Z" />
  </Icon>
)

export const SparkIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3.5c.7 4 1.8 5.3 5.8 6-4 .7-5.1 2-5.8 6-.7-4-1.8-5.3-5.8-6 4-.7 5.1-2 5.8-6Z" />
    <path d="M18.5 5.5c.2 1.2.6 1.6 1.8 1.8-1.2.2-1.6.6-1.8 1.8-.2-1.2-.6-1.6-1.8-1.8 1.2-.2 1.6-.6 1.8-1.8Z" />
  </Icon>
)

export const InfoIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </Icon>
)

export const CopyIcon = (props: IconProps) => (
  <Icon {...props}>
    <rect x="9" y="9" width="11" height="11" rx="2.5" />
    <path d="M15 5.5H6.5A2.5 2.5 0 0 0 4 8v8.5" />
  </Icon>
)

export const PlusIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 5.5v13M5.5 12h13" />
  </Icon>
)

export const MinusIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5.5 12h13" />
  </Icon>
)

export const TruckIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 7.5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1V16H3V7.5Z" />
    <path d="M14 10h3.3a1 1 0 0 1 .82.43L20.5 14v2H14v-6Z" />
    <circle cx="7" cy="17.5" r="1.8" />
    <circle cx="17" cy="17.5" r="1.8" />
  </Icon>
)

export const PackageIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="m12 3.5 7.5 4v9L12 20.5 4.5 16.5v-9L12 3.5Z" />
    <path d="m4.5 7.5 7.5 4 7.5-4M12 11.5v9" />
  </Icon>
)

export const UserIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="8.5" r="3.75" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </Icon>
)

export const ArrowLeftIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M19 12H5" />
    <path d="m11.5 18.5-6.5-6.5 6.5-6.5" />
  </Icon>
)

/* ── meal slots ──────────────────────────────────────────────────────────
   Breakfast/lunch/dinner/snack for the meal planner. Drawn on the same
   24×24, single-1.5px-stroke grid as the rest of the set so they carry equal
   optical weight beside the existing glyphs. */

/** Breakfast — sun rising over the horizon. */
export const BreakfastIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 18h18" />
    <path d="M7.5 18a4.5 4.5 0 0 1 9 0" />
    <path d="M12 5.5v2M5.6 8.1l1.4 1.4M18.4 8.1 17 9.5" />
  </Icon>
)

/** Lunch — fork and knife. A plate-from-above read too close to the concentric
 *  Dishes nav glyph, so cutlery carries the meal instead. */
export const LunchIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M7 3.5v6a2.5 2.5 0 0 0 5 0v-6" />
    <path d="M9.5 12v8.5" />
    <path d="M17 20.5V3.5c1.8 1 2.8 3 2.8 5.5S18.8 13 17 13.8" />
  </Icon>
)

/** Dinner — a cloche/serving dome on the table. */
export const DinnerIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3.5 17.5h17" />
    <path d="M5.5 14.5a6.5 6.5 0 0 1 13 0" />
    <path d="M12 8V6" />
  </Icon>
)

/** Snack — an apple. Earlier passes read as a waste bin (dangerous next to a
 *  remove control) and then as a face; fruit is unambiguous at 16px. */
export const SnackIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 8.5c-1.4-1.2-3.4-1.2-4.8 0-1.6 1.4-1.8 4-.7 6.4 1 2.2 2.6 3.8 3.9 3.8.6 0 1.1-.3 1.6-.3s1 .3 1.6.3c1.3 0 2.9-1.6 3.9-3.8 1.1-2.4.9-5-.7-6.4-1.4-1.2-3.4-1.2-4.8 0Z" />
    <path d="M12 8.5V5.5" />
    <path d="M12.5 5.5c1.4-1.6 3.3-1.5 3.3-1.5s.1 1.9-1.5 2.6" />
  </Icon>
)

export const EyeIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
)

export const EyeOffIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9.9 5.8A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a15.4 15.4 0 0 1-3 3.4" />
    <path d="M6.3 7.6A15.3 15.3 0 0 0 2.5 12S6 18.5 12 18.5a9.3 9.3 0 0 0 3.2-.6" />
    <path d="M10 10a3 3 0 0 0 4 4" />
    <path d="M4 4 20 20" />
  </Icon>
)

/* ── Culinary & Kitchen Craft Icons ───────────────────────────────────── */

export const CleaverIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 6.5h11a1 1 0 0 1 1 1v6a3 3 0 0 1-3 3H4a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z" />
    <path d="M16 11.5h4a1.5 1.5 0 0 1 1.5 1.5v0a1.5 1.5 0 0 1-1.5 1.5H16" />
    <circle cx="6.5" cy="9.5" r="0.8" />
  </Icon>
)

export const PanSkilletIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3.5 12.5a6.5 6.5 0 0 0 13 0v-1h-13v1Z" />
    <path d="M16.5 11.5l4.5-3" />
    <path d="M7 6.5c0 .8.5 1.2.5 2M11 5.5c0 .8.5 1.2.5 2" />
  </Icon>
)

export const ClocheIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 18.5h18" />
    <path d="M4.5 16.5a7.5 7.5 0 0 1 15 0" />
    <circle cx="12" cy="7" r="1.5" />
    <path d="M12 8.5v0.5" />
  </Icon>
)

export const DocketIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5 4.5h14v15l-2.5-1.5-2.5 1.5-2-1.5-2 1.5-2.5-1.5L5 19.5V4.5Z" />
    <path d="M8.5 8.5h7M8.5 12h7M8.5 15.5h4" />
  </Icon>
)

export const PotIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5 10.5h14v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7Z" />
    <path d="M4 10.5h16" />
    <path d="M2.5 12.5h2.5M19 12.5h2.5" />
    <path d="M9 7.5a3 3 0 0 1 6 0" />
  </Icon>
)

export const UtensilsIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M18 4v16M15 4v6a3 3 0 0 0 6 0V4" />
    <path d="M6 4v16M6 4a3 3 0 0 1 3 3v4a3 3 0 0 1-3 3M6 4a3 3 0 0 0-3 3v4a3 3 0 0 0 3 3" />
  </Icon>
)
